/**
 * Ressources i18next (D15).
 *
 * Sept espaces de noms, deux langues, le français d'abord. Les libellés de la
 * navigation viennent de 3.2 ; les statuts et les rôles viennent du modèle ;
 * les messages d'erreur sont rangés par code, et disent quoi faire (2.2).
 * En anglais : the back office, the website, the vault (D17).
 *
 * Ce paquet ne dépend pas d'i18next : il fournit les ressources et les options,
 * la régie et le moteur de site font l'appel à `init`.
 */
import { LANGUES } from '../model/commun.js';
import frPresentation from './fr/presentation.json' with { type: 'json' };
import enPresentation from './en/presentation.json' with { type: 'json' };
import frCommun from './fr/commun.json' with { type: 'json' };
import frOeuvres from './fr/oeuvres.json' with { type: 'json' };
import frContacts from './fr/contacts.json' with { type: 'json' };
import frCoffre from './fr/coffre.json' with { type: 'json' };
import frVentes from './fr/ventes.json' with { type: 'json' };
import frSite from './fr/site.json' with { type: 'json' };
import frErreurs from './fr/erreurs.json' with { type: 'json' };
import enCommun from './en/commun.json' with { type: 'json' };
import enOeuvres from './en/oeuvres.json' with { type: 'json' };
import enContacts from './en/contacts.json' with { type: 'json' };
import enCoffre from './en/coffre.json' with { type: 'json' };
import enVentes from './en/ventes.json' with { type: 'json' };
import enSite from './en/site.json' with { type: 'json' };
import enErreurs from './en/erreurs.json' with { type: 'json' };
/** Les espaces de noms du produit. */
export const NAMESPACES = [
    'commun',
    'oeuvres',
    'contacts',
    'coffre',
    'ventes',
    'site',
    'erreurs',
    'presentation',
];
/** L'espace de noms utilisé par défaut. */
export const NAMESPACE_PAR_DEFAUT = 'commun';
const fr = {
    commun: frCommun,
    oeuvres: frOeuvres,
    contacts: frContacts,
    coffre: frCoffre,
    ventes: frVentes,
    site: frSite,
    erreurs: frErreurs,
    presentation: frPresentation,
};
const en = {
    commun: enCommun,
    oeuvres: enOeuvres,
    contacts: enContacts,
    coffre: enCoffre,
    ventes: enVentes,
    site: enSite,
    erreurs: enErreurs,
    presentation: enPresentation,
};
/** Les ressources, dans la forme qu'attend i18next. */
export const ressources = { fr, en };
/** Alias anglais, pour l'appel à `i18next.init`. */
export const resources = ressources;
/**
 * Les options d'initialisation d'i18next.
 *
 * Le français est la langue de repli (D15). Les clés sont hiérarchiques, avec
 * le point pour séparateur : c'est ce qui permet de ranger les messages
 * d'erreur sous leur code, `erreurs:oeuvre.transition_interdite`.
 */
export const configurationI18next = {
    resources: ressources,
    lng: 'fr',
    fallbackLng: 'fr',
    supportedLngs: LANGUES,
    ns: NAMESPACES,
    defaultNS: NAMESPACE_PAR_DEFAUT,
    keySeparator: '.',
    nsSeparator: ':',
    interpolation: { escapeValue: false },
};
/** Lit une clé pointée dans un arbre de libellés. */
function lire(arbre, chemin) {
    let courant = arbre;
    for (const partie of chemin) {
        if (typeof courant !== 'object')
            return undefined;
        courant = courant[partie];
    }
    return typeof courant === 'string' ? courant : undefined;
}
/** Le libellé d'une clé, sans passer par i18next : utile à l'API et aux tests. */
export function libelle(namespace, cle, langue = 'fr') {
    const chemin = cle.split('.');
    return (lire(ressources[langue][namespace], chemin) ??
        lire(ressources.fr[namespace], chemin) ??
        `${namespace}:${cle}`);
}
/** Le message normalisé d'un code d'erreur. */
export function messageErreur(code, langue = 'fr') {
    return libelle('erreurs', code, langue);
}
/** Toutes les clés d'un arbre, sous leur forme pointée et triées. */
export function clesDe(arbre, prefixe = '') {
    const cles = [];
    for (const [cle, valeur] of Object.entries(arbre)) {
        const complete = prefixe === '' ? cle : `${prefixe}.${cle}`;
        if (typeof valeur === 'string') {
            cles.push(complete);
        }
        else {
            cles.push(...clesDe(valeur, complete));
        }
    }
    return cles.sort();
}
//# sourceMappingURL=index.js.map